import './ReferencePopover.css';

import { RefObject, useEffect, useMemo, useRef } from 'react';
import {
  ReferenceControl,
  ReferenceItem,
} from '../../../../../../../types/types';
import SeptimLoadingSpinner from '../../../../../SeptimLoadingSpinner/SeptimLoadingSpinner';
import Button from '../../../../../../Button/Button';
import Checkbox from '../../../../../../Checkbox/Checkbox';
import clsx from 'clsx';
import { IonItem, IonList } from '@ionic/react';
import { useTranslate } from '../../../../../../../hooks/context/useTranslate';
import { useListFocusNav } from '../../../../../../../hooks/useListFocusNav/useListFocusNav';

interface ReferencePopoverProps {
  control: ReferenceControl;
  closePopover: () => void;
  fetchReferenceData: () => void;
  data: any;
  isLoading: boolean;
  selectedItems: ReferenceItem[];
  onItemSelect: (newSelectedItems: ReferenceItem[]) => void;
  fetchError: Error | null;
  loadMoreData: () => void;
  listRef: RefObject<HTMLDivElement | null>;
  selectAllItems: (preserveFocus?: boolean) => void;
  // Container holding the parent's quick-filter input — Ctrl+A yields to typed text only inside it.
  quickFilterContainerRef?: RefObject<HTMLElement | null>;
  // Called when keyboard navigation moves focus above the first item so the parent can restore focus to the input.
  onLeaveTop?: () => void;
  formatLabel?: (value: string) => string;
}

// Module-scope identity so a missing formatLabel prop keeps a stable ref — an inline default would invalidate the items useMemo every render.
const identityLabel = (label: string) => label;

const ReferencePopover = ({
  control,
  closePopover,
  fetchReferenceData,
  data,
  isLoading,
  selectedItems,
  onItemSelect,
  fetchError,
  loadMoreData,
  listRef,
  selectAllItems,
  quickFilterContainerRef,
  onLeaveTop,
  formatLabel = identityLabel,
}: ReferencePopoverProps) => {
  const popoverContainerRef = useRef<HTMLDivElement>(null);
  // Tracks last hovered DOM target so Ctrl+A fires when the cursor is over the popover even if focus is parked elsewhere.
  const lastHoveredTargetRef = useRef<EventTarget | null>(null);
  const { t } = useTranslate();

  const isMultiselect = control.multiSelect;

  // `data` is an array only once a response resolved (null before), so the empty message can't flash while the first request is still on its way.
  const hasResolvedData: boolean = Array.isArray(data);

  // Derived directly from `data` — no useState/useEffect ping-pong that would force an extra render pass on every response.
  const items: ReferenceItem[] = useMemo(() => {
    if (!data) return [];
    return data.map((item: any) => {
      const value = item[control.idItem].toString();
      // formatLabel keys off labelItem || idItem, so the idItem fallback
      // (e.g. a dateTime idItem with no labelItem) must be formatted too.
      const rawLabel = control.labelItem
        ? String(item[control.labelItem])
        : value;
      return {
        value,
        label: formatLabel(rawLabel),
      };
    });
  }, [data, control.idItem, control.labelItem, formatLabel]);

  // Set-based lookup — with N items and M selected, isSelected on each render was O(N*M). Select-all on a large dataset made this a per-row hot path that blocked arrow-key navigation and scrolling.
  const selectedValueSet = useMemo(
    () => new Set(selectedItems.map((item) => item.value)),
    [selectedItems]
  );
  const isSelected = (value) => selectedValueSet.has(value);

  const reset = () => {
    onItemSelect([]);
  };

  const selectAll = (e: React.MouseEvent<HTMLElement>) => {
    // Prevent bubble: selectAllItems sets isLoading=true, detaching this button; react-tiny-popover
    // then sees an outside click on the detached node and closes the popover.
    e.stopPropagation();
    selectAllItems();
  };

  const onCheckboxSelected = (value, label, selected) => {
    if (selected) {
      onItemSelect([...selectedItems, { value, label }]);
    } else if (!selected) {
      onItemSelect(selectedItems.filter((v) => v.value !== value));
    }
  };

  const onSingleItemSelect = (value, label) => {
    onItemSelect([{ value, label }]);
    closePopover();
  };

  // Declarative onScroll: re-attaches on every mount, avoiding the case where
  // useEffect ran before the list div existed (isLoading=true → spinner only).
  const gridScrollHandler = (e: React.UIEvent<HTMLDivElement>) => {
    if (
      e.currentTarget.scrollTop + 400 >=
      e.currentTarget.scrollHeight - e.currentTarget.clientHeight
    ) {
      loadMoreData();
    }
  };

  const {
    focusedIndex,
    setFocusedIndex,
    setFocusedFromEvent,
    onContainerKeyDown,
  } = useListFocusNav({
    listRef,
    itemsLength: items.length,
    onLoadMore: loadMoreData,
    onLeaveTop,
    onSpace: isMultiselect
      ? (index) => {
          const item = items[index];
          if (!item) return;
          onCheckboxSelected(item.value, item.label, !isSelected(item.value));
        }
      : undefined,
    onEnter: !isMultiselect
      ? (index) => {
          const item = items[index];
          if (!item) return;
          onSingleItemSelect(item.value, item.label);
        }
      : undefined,
    onEscape: closePopover,
  });

  useEffect(() => {
    fetchReferenceData();
  }, []);

  // Ctrl/Cmd+A inside the popover triggers the "Select all" footer action. Multi-only — single-select has no notion of selecting multiple rows. Scope check uses focused element with mouse-hover fallback so hovering over the popup (without focus on it) also counts.
  useEffect(() => {
    if (!isMultiselect) return;

    const isInsideScope = () => {
      const container = popoverContainerRef.current;
      if (!container) return false;
      const active = document.activeElement;
      if (active && container.contains(active)) return true;
      const hovered = lastHoveredTargetRef.current as Node | null;
      return !!(hovered && container.contains(hovered));
    };

    const onKeyDown = (event: KeyboardEvent) => {
      const isSelectAll =
        (event.ctrlKey || event.metaKey) &&
        !event.shiftKey &&
        !event.altKey &&
        event.key.toLowerCase() === 'a';
      if (!isSelectAll) return;
      // A typed quick-filter turns Ctrl+A into the input's select-all (text + chips) — yield the chord, but only to this reference's own input, never to unrelated fields elsewhere on the page.
      const active = document.activeElement;
      if (
        quickFilterContainerRef?.current?.contains(active) &&
        (active instanceof HTMLInputElement ||
          active instanceof HTMLTextAreaElement) &&
        active.value !== ''
      ) {
        return;
      }
      // Ignore key-repeat while the user keeps Ctrl+A held — otherwise every repeat re-runs selectAllItems, thrashing state and focus.
      if (event.repeat) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (!isInsideScope()) return;
      event.preventDefault();
      event.stopPropagation();
      // preserveFocus tells selectAllItems to skip its default focusMultiSelectInputToEnd, so the user's row focus is never taken away. The .reference-row-focused CSS class (tied to focusedIndex) keeps the row visually highlighted since :focus-visible can be suppressed by the modifier chord.
      selectAllItems(true);
    };

    const onMouseMove = (event: MouseEvent) => {
      lastHoveredTargetRef.current = event.target;
    };

    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('mousemove', onMouseMove, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('mousemove', onMouseMove, true);
    };
  }, [isMultiselect, selectAllItems]);

  return (
    <div
      ref={popoverContainerRef}
      className={clsx(
        'reference-popover',
        isMultiselect && 'reference-popover-multiselect'
      )}
    >
      {isLoading || (!hasResolvedData && !fetchError) ? (
        <div className="reference-loading-container">
          <SeptimLoadingSpinner />
        </div>
      ) : (
        <>
          <div
            className="reference-list"
            ref={listRef}
            onScroll={gridScrollHandler}
            onKeyDown={onContainerKeyDown}
          >
            <IonList className="checkbox-list">
              {fetchError || items.length === 0 ? (
                <IonItem lines="none" className="reference-disabled-item">
                  {t(
                    fetchError
                      ? 'errors.referenceLoadingError'
                      : 'noRecordFound'
                  )}
                </IonItem>
              ) : (
                items.map(({ value, label }, index) => {
                  const onRowFocus = (event: { currentTarget: HTMLElement }) =>
                    setFocusedFromEvent(event, index);

                  if (isMultiselect) {
                    return (
                      <IonItem
                        lines="none"
                        key={`${value}-${index}`}
                        tabIndex={0}
                        onFocus={onRowFocus}
                        // Mouse click on the checkbox may not bubble focus to the row (Ionic web-component shadow DOM), so also promote the clicked row to focused explicitly to keep the visual highlight consistent with keyboard nav.
                        onClick={() => setFocusedIndex(index)}
                        className={clsx(
                          focusedIndex === index && 'reference-row-focused'
                        )}
                      >
                        <Checkbox
                          value={isSelected(value)}
                          onChange={(selected) =>
                            onCheckboxSelected(value, label, selected)
                          }
                          label={label}
                          color="secondary"
                        />
                      </IonItem>
                    );
                  }
                  return (
                    <IonItem
                      lines="none"
                      key={`${value}-${index}`}
                      button
                      tabIndex={0}
                      onFocus={onRowFocus}
                      onClick={() => onSingleItemSelect(value, label)}
                      className={clsx(
                        isSelected(value) && 'selected-item',
                        focusedIndex === index && 'reference-row-focused'
                      )}
                    >
                      {label}
                    </IonItem>
                  );
                })
              )}
            </IonList>
          </div>
          {isMultiselect && (
            <div className="select-filters-footer">
              <Button onClick={reset} fill="clear" color="dark">
                {t('reset')}
              </Button>
              <Button onClick={selectAll} fill="clear" color="dark">
                {t('selectAll')}
              </Button>
              <Button
                onClick={closePopover}
                fill="clear"
                color="secondary"
                className="submit-btn"
              >
                {t('apply')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReferencePopover;
